import { customerInvoiceListQuerySchema, type Currency } from '@chukua/common';
import { Select } from 'antd';
import { useCallback } from 'react';
import { useRemoteSearchOptions } from '../../../../../shared/hooks/use-remote-search-options';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { listCustomerInvoices } from '../payment-promises.api';
import { isInvoiceSelectable } from '../policies/promise-form';

const PAGE_SIZE = 20;

export interface InvoiceChoice {
  value: string;
  label: string;
  currency: Currency;
}

interface InvoiceSearchSelectProps {
  /** Passed by the form item, so the label points at the field. */
  id?: string;
  customerId: string;
  value?: InvoiceChoice[];
  onChange?: (choices: InvoiceChoice[]) => void;
}

/**
 * Remote-search picker over the customer's issued invoices (`GET /customers/:id/invoices?q=`), opening on the first page. A promise has one
 * currency, so once an invoice is picked, invoices in another currency are shown but cannot be chosen.
 */
export function InvoiceSearchSelect({ id, customerId, value = [], onChange }: InvoiceSearchSelectProps) {
  const fetchInvoices = useCallback(
    (q: string | undefined) =>
      listCustomerInvoices(customerId, customerInvoiceListQuerySchema.parse({ q, pageSize: PAGE_SIZE })).then((page) =>
        page.items.map((invoice) => ({
          value: invoice.id,
          label: `${invoice.invoiceNumber ?? 'Unnumbered'} · ${formatMoney({ amountMinor: invoice.grossAmountMinor, currency: invoice.currency })}`,
          currency: invoice.currency,
        })),
      ),
    [customerId],
  );
  const { selectProps } = useRemoteSearchOptions(fetchInvoices);
  const pickedCurrency = value[0]?.currency ?? null;

  /** A newly picked invoice is always one the dropdown is showing; earlier picks are already in `value`. */
  function choose(picked: { value: string }[]) {
    const choices = picked.map(({ value: id }) => selectProps.options.find((option) => option.value === id) ?? value.find((choice) => choice.value === id));
    onChange?.(choices.filter((choice): choice is InvoiceChoice => choice !== undefined));
  }

  return (
    <Select
      {...selectProps}
      id={id}
      mode="multiple"
      labelInValue
      value={value}
      options={selectProps.options.map((option) => ({ ...option, disabled: !isInvoiceSelectable(option.currency, pickedCurrency) }))}
      onChange={choose}
      placeholder="Select or search invoices"
      notFoundContent={selectProps.loading ? 'Searching…' : 'No issued invoices found'}
    />
  );
}
